import { useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { noop, get, isEqual } from 'lodash';
import { Form } from 'react-bootstrap';
import { Controller } from 'react-hook-form';
import ErrorMessage from 'components/Common/ErrorMessage';
import { useNodeContext } from 'contexts/NodeProvider';
import { formatKey } from '../../FormatKeys';

const RadioQuestion = forwardRef(
    (
        { name, question, options, value, onChange, error, required, disabled },
        ref
    ) => {
        return (
            <div className="radio-field-container" data-testid={name}>
                <span className="radio-question">
                    {question}
                    <span className="text-danger">{required ? ' *' : ' '}</span>
                </span>
                <div
                    ref={ref}
                    className="radio-group"
                    role="radiogroup"
                    aria-labelledby={`label-radiogroup`}
                >
                    {options.map((option, i) => (
                        <div key={`radio-option-${i}`} className="radio-field">
                            <Form.Check>
                                <Form.Check.Input
                                    data-testid={formatKey(
                                        `${name}-${option.label}`
                                    )}
                                    type="radio"
                                    size="lg"
                                    checked={isEqual(value, option.value)}
                                    name={`${name}-${option.label}`}
                                    onChange={(_) => onChange(option.value)}
                                    id={formatKey(`${name}-${option.label}`)}
                                    value={option.value}
                                    aria-label={`${name}-${option.label}`}
                                    disabled={disabled}
                                />
                                <Form.Check.Label
                                    key={`radio-option-${i}`}
                                    className="radio-label"
                                    htmlFor={formatKey(
                                        `${name}-${option.label}`
                                    )}
                                    tabIndex={0}
                                    onKeyUp={() => onChange(option.value)}
                                >
                                    <span>{option.label}</span>
                                </Form.Check.Label>
                            </Form.Check>
                        </div>
                    ))}
                </div>
                <ErrorMessage message={error} />
            </div>
        );
    }
);

const RadiosWithController = forwardRef(
    (
        {
            control,
            name,
            defaultValue,
            question,
            options,
            required,
            onChange = noop,
            registerRef = true,
            disabled,
        },
        ref
    ) => {
        const _ref = useRef();
        const { nodesRef } = useNodeContext();

        useImperativeHandle(ref, () => _ref.current);

        return (
            <Controller
                control={control}
                name={name}
                defaultValue={defaultValue}
                render={({
                    field: { onChange: handleChange, value, ref: rhfRef },
                    fieldState: { error },
                }) => {
                    const onRefChange = useCallback((node) => {
                        if (registerRef) {
                            _ref.current = node;
                            rhfRef = _ref;
                            if (nodesRef) {
                                nodesRef.current[name] = node;
                            }
                        }
                    }, []);

                    return (
                        <RadioQuestion
                            ref={onRefChange}
                            name={name}
                            onChange={(val) => {
                                handleChange(val);
                                onChange(val);
                            }}
                            value={value}
                            question={question}
                            options={options}
                            error={get(error, 'message')}
                            required={required}
                            disabled={disabled}
                        />
                    );
                }}
            />
        );
    }
);
export default RadiosWithController;
